import React, { KeyboardEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DraftHandleValue, EditorCommand, EditorState, getDefaultKeyBinding } from 'draft-js';
import { Key } from 'ts-key-enum';
import Editor from '@draft-js-plugins/editor';

import './index.less';

import IbIcon from '../common/IbIcon';
import IbButton from '../common/IbButton';
import IbTooltip from '../common/IbTooltip';
import JsonEditorExpandable from '../common/JsonEditorExpandable';
import { editorStateToMarkdown } from '../../utils/markdownEditor';
import { isTouchOnlyDevice } from '../../utils/browserUtil';
import {
  ChatMessageContentInner,
  DataInterchangeFormat,
  DataInterfaceInput,
  DataInterfaceType,
  DataPayload,
  DataPayloadType,
} from '../../../gamma-api';
import { CodeEditorStatus } from '../common/CodeEditor/types';
import { DEFAULT_EDITOR_STATUS } from '../common/CodeEditor/const';

import {
  CONTROLS_CLASS_NAME,
  CONTROLS_EXTRA_CLASS_NAME,
  CONTROLS_MAIN_CLASS_NAME,
  EDITOR_SEND_MESSAGE_COMMAND,
  FOOTER_CLASS_NAME,
  HEADER_CLASS_NAME,
  MAIN_CLASS_NAME,
} from './const';

export interface IMessageInputProps {
  messageSendingEnabled: boolean;
  dataInterface?: DataInterfaceInput;
  codeFolder?: string;
  editorPrefix?: ReactNode;
  editorSuffix?: ReactNode;
  sendControlsPrefix?: ReactNode;
  onMessageSend?: (content: ChatMessageContentInner, payload: DataPayload) => void;
}

const MessageInput: React.FC<IMessageInputProps> = ({
  messageSendingEnabled,
  dataInterface,
  codeFolder,
  editorPrefix,
  editorSuffix,
  sendControlsPrefix,
  onMessageSend,
}) => {
  const { t } = useTranslation();

  const [textEditorState, setTextEditorState] = useState(EditorState.createEmpty());
  const [jsonEditorStatus, setJsonEditorStatus] = useState<CodeEditorStatus>(DEFAULT_EDITOR_STATUS);
  const [jsonEditorExpanded, setJsonEditorExpanded] = useState(false);

  const textMessage = editorStateToMarkdown(textEditorState);
  const [jsonMessage, setJsonMessage] = useState<string>();

  const [messageContent, setMessageContent] = useState<ChatMessageContentInner>();
  const [messagePayload, setMessagePayload] = useState<DataPayload>();

  const isStructuredData =
    dataInterface?.type === DataInterfaceType.Api && dataInterface?.input.data_format !== DataInterchangeFormat.Text;
  const messageData = isStructuredData ? jsonMessage : textMessage;

  const onMessageChange = () => {
    if (!messageData) {
      setJsonEditorStatus(DEFAULT_EDITOR_STATUS);
      setMessageContent(undefined);
      setMessagePayload(undefined);
      return;
    }

    let content;
    let payload;
    try {
      content = { block_type: 'text', text: messageData } as ChatMessageContentInner;
      payload = isStructuredData
        ? { type: DataPayloadType.Json, data: JSON.parse(messageData) }
        : { type: DataPayloadType.Text, data: messageData };
    } catch {
      setJsonEditorStatus(CodeEditorStatus.Error);
      return;
    }

    setMessageContent(content);
    setMessagePayload(payload);
  };

  useEffect(onMessageChange, [textMessage, jsonMessage]);

  const resetTextEditor = () => {
    const newEditorState = EditorState.createEmpty();
    setTextEditorState(EditorState.moveFocusToEnd(newEditorState));
    setJsonMessage(undefined);
  };

  const trySendMessage = () => {
    if (!messageSendingEnabled || !messageContent || !messagePayload) {
      return;
    }

    onMessageSend?.(messageContent, messagePayload);
    resetTextEditor();
  };

  const keyBindingFn = (e: KeyboardEvent): string | null | undefined => {
    if (e.key === Key.ArrowUp || e.key === Key.ArrowDown || e.key === Key.Escape) {
      return undefined;
    }

    if (!e.ctrlKey && !e.shiftKey && e.key === Key.Enter && !isTouchOnlyDevice()) {
      return EDITOR_SEND_MESSAGE_COMMAND;
    }

    return getDefaultKeyBinding(e);
  };

  const onSendButtonClick = () => trySendMessage();

  const onExpandButtonClick = () => setJsonEditorExpanded(true);

  const handleKeyCommand = (command: EditorCommand): DraftHandleValue => {
    if (command === EDITOR_SEND_MESSAGE_COMMAND) {
      trySendMessage();
      return 'handled';
    }
    return 'not-handled';
  };

  const onTextEditorChange = (newEditorState: EditorState) => {
    // HACK: https://github.com/facebookarchive/draft-js/issues/1198#issuecomment-535651492
    const currentContentTextLength = textEditorState.getCurrentContent().getPlainText().length;
    const newContentTextLength = newEditorState.getCurrentContent().getPlainText().length;
    if (currentContentTextLength === 0 && newContentTextLength === 1) {
      // WORKAROUND: listens to input changes and focuses/moves cursor to back after typing in first character
      setTextEditorState(EditorState.moveFocusToEnd(newEditorState));
    } else {
      setTextEditorState(newEditorState);
    }
  };

  const onJsonEditorChange = (value?: string) => {
    setJsonMessage(value);
  };

  const onJsonEditorValidate = (errors: string[], status: CodeEditorStatus) => {
    setJsonEditorStatus(status);
  };

  const onJsonEditorCollapse = () => {
    setJsonEditorExpanded(false);
  };

  const renderTextEditor = () => {
    return (
      <Editor
        spellCheck
        stripPastedStyles
        editorState={textEditorState}
        handleKeyCommand={handleKeyCommand}
        keyBindingFn={keyBindingFn}
        placeholder={t('Chat: Enter your message...')}
        onChange={onTextEditorChange}
      />
    );
  };

  const renderJsonEditor = () => {
    const schema =
      dataInterface?.input.data_format === DataInterchangeFormat.Schema ? dataInterface?.input.json_schema : null;

    return (
      <JsonEditorExpandable
        codeFolder={codeFolder}
        expanded={jsonEditorExpanded}
        schema={schema}
        status={jsonEditorStatus}
        toolbar={jsonEditorExpanded}
        value={jsonMessage}
        onChange={onJsonEditorChange}
        onCollapse={onJsonEditorCollapse}
        onValidate={onJsonEditorValidate}
      />
    );
  };

  const renderEditor = () => {
    return isStructuredData ? renderJsonEditor() : renderTextEditor();
  };

  const renderSendButton = () => {
    let tooltip = t('Chat: Controls: Send: Tooltip');
    if (!messageSendingEnabled) {
      tooltip = t('Chat: Controls: Send: Disabled');
    } else if (isStructuredData) {
      if (jsonEditorStatus === DEFAULT_EDITOR_STATUS) {
        tooltip = t('Chat: Controls: Send: JSON: Empty');
      } else if (jsonEditorStatus === CodeEditorStatus.Error) {
        tooltip = t('Chat: Controls: Send: JSON: Error');
      } else if (jsonEditorStatus === CodeEditorStatus.Success) {
        tooltip = t('Chat: Controls: Send: JSON: Tooltip');
      }
    } else {
      if (!textMessage) {
        tooltip = t('Chat: Controls: Send: Text: Empty');
      } else {
        tooltip = t('Chat: Controls: Send: Text: Tooltip');
      }
    }

    const disabled =
      !messageSendingEnabled || (isStructuredData ? jsonEditorStatus !== CodeEditorStatus.Success : !textMessage);
    return (
      <IbTooltip title={tooltip}>
        <IbButton disabled={disabled} icon={<IbIcon iconName="send" />} type="icon" onClick={onSendButtonClick} />
      </IbTooltip>
    );
  };

  const renderExpandButton = () => {
    if (!isStructuredData) {
      return null;
    }

    return (
      <IbTooltip title={t('Chat: Controls: Expand: JSON: Tooltip')}>
        <IbButton
          disabled={jsonEditorExpanded}
          icon={<IbIcon iconName="fullscreen-two" />}
          type="icon"
          onClick={onExpandButtonClick}
        />
      </IbTooltip>
    );
  };

  const renderControls = () => {
    return (
      <div className={CONTROLS_CLASS_NAME}>
        <div className={CONTROLS_MAIN_CLASS_NAME}>{renderSendButton()}</div>
        {sendControlsPrefix}
        <div className={CONTROLS_EXTRA_CLASS_NAME}>{renderExpandButton()}</div>
      </div>
    );
  };

  const renderHeader = () => {
    if (!editorPrefix) {
      return null;
    }

    return <div className={HEADER_CLASS_NAME}>{editorPrefix}</div>;
  };

  const renderFooter = () => {
    return (
      <div className={FOOTER_CLASS_NAME}>
        {editorSuffix}
        {renderControls()}
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderHeader()}
      {renderEditor()}
      {renderFooter()}
    </div>
  );
};

export default MessageInput;
